import React, { useState } from 'react'

const EMAIL = 'contact@zentarq.com'
const COMPANY = 'Tamigo'

const appSteps = [
  { title: 'Open the app', text: 'Launch the mobile application on your device.' },
  { title: 'Log in', text: 'Sign in to the account you want to delete.' },
  { title: 'Open your settings', text: 'Go to Profile or Account Settings.' },
  { title: 'Select Delete Account', text: 'Tap Delete Account to start the request.' },
  { title: 'Confirm', text: 'Confirm the request and follow any extra instructions shown in the app.' },
]

const requestFields = [
  { label: 'Registered name', required: true },
  { label: 'Registered email address or mobile number', required: true },
  { label: 'Reason for deletion', required: false },
]

const removed = [
  'Your user account is deleted.',
  'Personal information linked to your account is deleted or anonymized where applicable.',
]

const retained = [
  'Some information may be kept for legal, regulatory, security, fraud-prevention, or accounting purposes, for as long as the law requires.',
]

const mailto =
  `mailto:${EMAIL}?subject=${encodeURIComponent('Account deletion request')}` +
  `&body=${encodeURIComponent(
    'Registered name:\nRegistered email or mobile number:\nReason for deletion (optional):\n'
  )}`

const Deleteaccount = () => {
  const [copied, setCopied] = useState(false)

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (e) {
      setCopied(false)
    }
  }

  return (
    <div className="da">
      <style>{css}</style>

      <header className="da-hero">
        <div className="da-wrap">
          <p className="da-brand">{COMPANY}</p>
          <h1>Delete your account</h1>
          <p className="da-lead">
            You can delete your account and its personal data yourself in the app, or ask us to do
            it for you. Both options are below.
          </p>
          <div className="da-path" aria-label="Where to find it in the app">
            <span>Profile</span>
            <i aria-hidden="true">›</i>
            <span>Account Settings</span>
            <i aria-hidden="true">›</i>
            <span className="da-path-end">Delete Account</span>
          </div>
        </div>
      </header>

      <main className="da-wrap da-main">
        {/* Option 1 */}
        <section className="da-section" aria-labelledby="opt1">
          <div className="da-side">
            <span className="da-tag">Option 1</span>
            <h2 id="opt1">Delete from the app</h2>
            <p>The fastest way. Takes about a minute.</p>
          </div>
          <ol className="da-steps">
            {appSteps.map((s, i) => (
              <li key={s.title}>
                <span className="da-num">{i + 1}</span>
                <div>
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* Option 2 */}
        <section className="da-section" aria-labelledby="opt2">
          <div className="da-side">
            <span className="da-tag">Option 2</span>
            <h2 id="opt2">Ask us to delete it</h2>
            <p>Can't get into the app? Email us and we'll verify and process your request.</p>
          </div>
          <div className="da-card">
            <h3>Include in your email</h3>
            <ul className="da-fields">
              {requestFields.map((f) => (
                <li key={f.label}>
                  <span>{f.label}</span>
                  <em className={f.required ? 'req' : 'opt'}>{f.required ? 'Required' : 'Optional'}</em>
                </li>
              ))}
            </ul>
            <div className="da-actions">
              <a className="da-btn da-btn-primary" href={mailto}>
                Email deletion request
              </a>
              <button type="button" className="da-btn" onClick={copyEmail}>
                {copied ? 'Copied' : 'Copy email address'}
              </button>
            </div>
            <p className="da-small">
              Sent to <strong>{EMAIL}</strong>
            </p>
          </div>
        </section>

        {/* After deletion */}
        <section className="da-section" aria-labelledby="after">
          <div className="da-side">
            <h2 id="after">After deletion</h2>
            <p>What happens to your data once we process the request.</p>
          </div>
          <div className="da-after">
            <div className="da-panel">
              <h3>Removed</h3>
              <ul>
                {removed.map((t) => (
                  <li key={t}>
                    <svg viewBox="0 0 20 20" aria-hidden="true">
                      <path d="M4 10.5l4 4 8-9" />
                    </svg>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="da-panel">
              <h3>May be retained</h3>
              <ul>
                {retained.map((t) => (
                  <li key={t}>
                    <svg viewBox="0 0 20 20" aria-hidden="true">
                      <circle cx="10" cy="10" r="6.5" />
                      <path d="M10 6.8v3.6M10 13.3v.1" />
                    </svg>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="da-warn" role="note">
              <strong>This can't be undone.</strong> Once deletion is complete, your account
              information cannot be recovered.
            </div>
          </div>
        </section>
      </main>

      <footer className="da-footer">
        <div className="da-wrap da-footer-in">
          <div>
            <h2>Questions about your data?</h2>
            <p>Contact us and we'll help.</p>
          </div>
          <div className="da-contact">
            <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
            <span>{COMPANY}</span>
          </div>
        </div>
      </footer>
    </div>
  )
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,500;12..96,700&family=Figtree:wght@400;500;600&display=swap');

.da{
  --bg:#F2F5F8; --surface:#FFFFFF; --ink:#16263B; --muted:#5A6B80; --line:#DCE3EB;
  --brand:#0E6B63; --brand-dark:#0A544E; --brand-soft:#E3F2F0;
  --warn:#9A3412; --warn-soft:#FDEEE4;
  font-family:'Figtree',system-ui,sans-serif; color:var(--ink); background:var(--bg);
  min-height:100vh; line-height:1.6; -webkit-font-smoothing:antialiased;
}
.da *{box-sizing:border-box}
.da h1,.da h2,.da h3{font-family:'Bricolage Grotesque','Figtree',sans-serif; margin:0; line-height:1.15}
.da p{margin:0}
.da-wrap{max-width:1040px; margin:0 auto; padding:0 24px}

/* Hero */
.da-hero{background:var(--ink); color:#fff; padding:64px 0 56px}
.da-brand{font-weight:600; color:#8FD3CB; margin-bottom:20px; font-size:15px}
.da-hero h1{font-size:clamp(36px,6vw,60px); font-weight:700; letter-spacing:-.02em; max-width:14ch}
.da-lead{margin-top:20px; max-width:56ch; color:#C4D0DE; font-size:18px}
.da-path{display:inline-flex; flex-wrap:wrap; align-items:center; gap:10px; margin-top:32px;
  padding:10px 14px; border:1px solid rgba(255,255,255,.18); border-radius:12px; font-weight:500; font-size:15px}
.da-path i{font-style:normal; opacity:.5}
.da-path-end{background:#8FD3CB; color:var(--ink); padding:3px 10px; border-radius:8px; font-weight:600}

/* Sections */
.da-main{padding-top:56px; padding-bottom:24px}
.da-section{display:grid; grid-template-columns:280px 1fr; gap:48px; padding:40px 0; border-top:1px solid var(--line)}
.da-section:first-child{border-top:0; padding-top:0}
.da-side h2{font-size:28px; font-weight:700; letter-spacing:-.01em}
.da-side p{margin-top:10px; color:var(--muted)}
.da-tag{display:inline-block; margin-bottom:12px; padding:2px 10px; border-radius:999px;
  background:var(--brand-soft); color:var(--brand-dark); font-size:13px; font-weight:600}

/* Steps */
.da-steps{list-style:none; margin:0; padding:0; position:relative}
.da-steps li{display:flex; gap:18px; padding-bottom:26px; position:relative}
.da-steps li:last-child{padding-bottom:0}
.da-steps li:not(:last-child)::before{content:''; position:absolute; left:17px; top:38px; bottom:2px; width:2px; background:var(--line)}
.da-num{flex:none; width:36px; height:36px; border-radius:50%; background:var(--brand); color:#fff;
  display:grid; place-items:center; font-weight:600; font-size:15px}
.da-steps h3{font-size:19px; font-weight:600; margin-top:5px}
.da-steps p{color:var(--muted); margin-top:2px}

/* Request card */
.da-card{background:var(--surface); border:1px solid var(--line); border-radius:16px; padding:28px}
.da-card h3{font-size:20px; font-weight:600}
.da-fields{list-style:none; margin:16px 0 0; padding:0}
.da-fields li{display:flex; justify-content:space-between; align-items:center; gap:16px;
  padding:12px 0; border-bottom:1px solid var(--line)}
.da-fields li:last-child{border-bottom:0}
.da-fields em{font-style:normal; font-size:13px; font-weight:600; padding:2px 10px; border-radius:999px; white-space:nowrap}
.da-fields .req{background:var(--brand-soft); color:var(--brand-dark)}
.da-fields .opt{background:#EEF1F5; color:var(--muted)}
.da-actions{display:flex; flex-wrap:wrap; gap:12px; margin-top:22px}
.da-btn{font:inherit; font-weight:600; font-size:15px; cursor:pointer; text-decoration:none;
  padding:12px 20px; border-radius:10px; border:1px solid var(--line); background:var(--surface); color:var(--ink);
  transition:background .15s, border-color .15s}
.da-btn:hover{background:#F2F5F8; border-color:#C5CFDB}
.da-btn-primary{background:var(--brand); border-color:var(--brand); color:#fff}
.da-btn-primary:hover{background:var(--brand-dark); border-color:var(--brand-dark)}
.da-small{margin-top:16px; font-size:14px; color:var(--muted)}
.da-small strong{color:var(--ink); font-weight:600}

/* After deletion */
.da-after{display:grid; grid-template-columns:1fr 1fr; gap:16px}
.da-panel{background:var(--surface); border:1px solid var(--line); border-radius:16px; padding:24px}
.da-panel h3{font-size:19px; font-weight:600; margin-bottom:12px}
.da-panel ul{list-style:none; margin:0; padding:0; display:grid; gap:12px}
.da-panel li{display:flex; gap:10px; color:#33465C; font-size:15px}
.da-panel svg{flex:none; width:20px; height:20px; margin-top:2px; fill:none; stroke-width:2; stroke-linecap:round; stroke-linejoin:round}
.da-panel:first-child svg{stroke:var(--brand)}
.da-panel:last-of-type svg{stroke:#B7791F}
.da-warn{grid-column:1 / -1; background:var(--warn-soft); color:var(--warn); border-radius:12px; padding:16px 20px; font-size:15px}

/* Footer */
.da-footer{margin-top:40px; background:var(--surface); border-top:1px solid var(--line); padding:48px 0}
.da-footer-in{display:flex; justify-content:space-between; align-items:center; gap:24px; flex-wrap:wrap}
.da-footer h2{font-size:24px; font-weight:700}
.da-footer p{color:var(--muted); margin-top:4px}
.da-contact{display:flex; flex-direction:column; align-items:flex-end; gap:2px}
.da-contact a{color:var(--brand-dark); font-weight:600; font-size:18px; text-underline-offset:4px}
.da-contact span{color:var(--muted); font-size:14px}

.da a:focus-visible,.da button:focus-visible{outline:3px solid #7CC9BF; outline-offset:3px}

@media (max-width:820px){
  .da-hero{padding:48px 0 40px}
  .da-section{grid-template-columns:1fr; gap:24px; padding:32px 0}
  .da-after{grid-template-columns:1fr}
  .da-contact{align-items:flex-start}
}
@media (prefers-reduced-motion:reduce){ .da *{transition:none !important} }
`

export default Deleteaccount